import {
  DocumentTextIcon,
  MicrophoneIcon,
  PhotoIcon,
  VideoCameraIcon,
} from '@heroicons/react/16/solid';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { forwardRef, useState } from 'react';
import { Button, Loading } from 'react-daisyui';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Download from 'yet-another-react-lightbox/plugins/download';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Video from 'yet-another-react-lightbox/plugins/video';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import { MediaType } from '../../../../../Chat.enum.ts';
import {
  formatDate,
  formatTime,
  parseSlides,
} from '../../../../../Chat.functions.ts';
import { useChatContext } from '../../../../../Chat.hook.ts';
import { tAttachment, tChatEvent } from '../../../../../Chat.types.ts';
import useNotesAction from '../../../../MessageBar/components/NotesAction/NotesAction.hook.ts';
import './Note.styles.scss';

const Note = forwardRef<HTMLDivElement, tChatEvent>(function Note(
  props: tChatEvent,
  ref,
) {
  const { selectedChat, setSelectedNote } = useChatContext();
  const { deleteEventNote, loading } = useNotesAction();

  const [openLightbox, setOpenLightbox] = useState<boolean>(false);

  function onCloseLightbox() {
    setOpenLightbox(false);
  }

  const MediaIcon = ({ attachment }: { attachment: tAttachment }) =>
    ((attachment.mediaType === MediaType.IMAGE ||
      attachment.mediaType === MediaType.STICKER) && (
      <PhotoIcon className="w-5 text-primary" />
    )) ||
    (attachment.mediaType === MediaType.VIDEO && (
      <VideoCameraIcon className="w-5 text-primary" />
    )) ||
    (attachment.mediaType === MediaType.AUDIO && (
      <MicrophoneIcon className="w-5 text-primary" />
    )) ||
    (attachment.mediaType === MediaType.DOCUMENT && (
      <DocumentTextIcon className="w-5 text-primary" />
    )) ||
    null;

  return (
    <div
      ref={ref}
      tabIndex={0}
      className={`Note ${props.selectedIndex === props.index && 'bg-primary/10'}`}
    >
      <div className="Note__Header">
        <h1 className="Note__Header__Title">{props.note?.title}</h1>
        <div className="Note__Header__Actions">
          <Button
            size="xs"
            color="ghost"
            shape="circle"
            onClick={() => {
              setSelectedNote({
                id: props.id,
                title: props.note?.title || '',
                content: props.content?.text || '',
                attachments: props.content?.attachments || undefined,
              });
            }}
          >
            <PencilIcon className="Note__Header__Actions__Icon" />
          </Button>
          <Button
            size="xs"
            color="ghost"
            shape="circle"
            onClick={() => {
              deleteEventNote(props.id);
            }}
          >
            {loading ? (
              <Loading className="Note__Header__Actions__Icon" />
            ) : (
              <TrashIcon className="Note__Header__Actions__Icon" />
            )}
          </Button>
        </div>
      </div>
      <p className="break-words">{props.content?.text}</p>
      {props.content?.attachments?.length ? (
        <div
          className={`flex gap-2 ${props.content.attachments.length > 1 ? 'justify-end' : ''}`}
          onClick={() => setOpenLightbox(true)}
        >
          <div className="flex gap-2 items-center border rounded-badge w-9/12 py-[2px] px-4">
            <MediaIcon attachment={props.content.attachments[0]} />
            <p className="truncate">
              {props.content.attachments[0].filename ||
                `${props.content.attachments[0].mimeType?.split('/')?.[1]} File`}
            </p>
          </div>

          {props.content.attachments.length > 1 ? (
            <div className="text-sm border rounded-full w-8 h-8 flex justify-center items-center">
              <p>{`+${props.content.attachments.length - 1}`}</p>
            </div>
          ) : null}

          {openLightbox && (
            <Lightbox
              plugins={[
                Zoom,
                Captions,
                Thumbnails,
                Download,
                Fullscreen,
                Video,
              ]}
              open={openLightbox}
              close={onCloseLightbox}
              slides={parseSlides(props.content.attachments)}
              thumbnails={{ hidden: true }}
            />
          )}
        </div>
      ) : null}
      <div className="Note__Bottom">
        <span className="Note__Header__Title">
          {
            selectedChat?.lead?.users?.find(
              (user) => user.auth_id === props.createdBy,
            )?.name
          }
        </span>{' '}
        | {formatDate(props.createdAt)} | {formatTime(props.createdAt)}
      </div>
    </div>
  );
});

export default Note;
